// Dynamic geocoding service for any city
// Falls back to multiple services when Google APIs fail

// Cache for coordinates to avoid repeated API calls
const coordinateCache = new Map();

// Fallback geocoding services
const geocodingServices = {
  // Primary: Google Geocoding API
  async google(cityName) {
    if (!window.google || !window.google.maps) {
      throw new Error('Google Maps not loaded');
    }

    return new Promise((resolve, reject) => {
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ address: cityName }, (results, status) => {
        if (status === 'OK' && results[0]) {
          const location = results[0].geometry.location;
          resolve({
            lat: location.lat(),
            lng: location.lng(),
            source: 'google'
          });
        } else {
          reject(new Error(`Google geocoding failed: ${status}`));
        }
      });
    });
  },

  // Fallback 1: OpenStreetMap Nominatim (free)
  async nominatim(cityName) {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(cityName)}&format=json&limit=1&addressdetails=1`,
        {
          headers: {
            'User-Agent': 'TripTribe/1.0'
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Nominatim API error: ${response.status}`);
      }

      const data = await response.json();
      if (data && data.length > 0) {
        return {
          lat: parseFloat(data[0].lat),
          lng: parseFloat(data[0].lon),
          source: 'nominatim'
        };
      }
      throw new Error('No results from Nominatim');
    } catch (error) {
      throw new Error(`Nominatim geocoding failed: ${error.message}`);
    }
  },

  // Fallback 2: MapBox Geocoding (if API key available)
  async mapbox(cityName) {
    const mapboxToken = process.env.REACT_APP_MAPBOX_TOKEN;
    if (!mapboxToken) {
      throw new Error('Mapbox token not configured');
    }

    try {
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(cityName)}.json?access_token=${mapboxToken}&limit=1`
      );

      if (!response.ok) {
        throw new Error(`Mapbox API error: ${response.status}`);
      }

      const data = await response.json();
      if (data.features && data.features.length > 0) {
        const [lng, lat] = data.features[0].center;
        return {
          lat,
          lng,
          source: 'mapbox'
        };
      }
      throw new Error('No results from Mapbox');
    } catch (error) {
      throw new Error(`Mapbox geocoding failed: ${error.message}`);
    }
  },

  // Fallback 3: Backend geocoding service
  async backend(cityName) {
    try {
      const response = await fetch(`/api/geocoding?city=${encodeURIComponent(cityName)}`);
      
      if (!response.ok) {
        throw new Error(`Backend geocoding error: ${response.status}`);
      }

      const data = await response.json();
      if (data.success && data.coordinates) {
        return {
          lat: data.coordinates.lat,
          lng: data.coordinates.lng,
          source: 'backend'
        };
      }
      throw new Error('No coordinates from backend');
    } catch (error) {
      throw new Error(`Backend geocoding failed: ${error.message}`);
    }
  }
};

// Main geocoding function with fallbacks
export const getCityCoordinates = async (cityName) => {
  if (!cityName || typeof cityName !== 'string') {
    throw new Error('Invalid city name provided');
  }

  const normalizedName = cityName.toLowerCase().trim();
  
  // Check cache first
  if (coordinateCache.has(normalizedName)) {
    console.log(`📍 Using cached coordinates for: ${cityName}`);
    return coordinateCache.get(normalizedName);
  }

  console.log(`🔍 Geocoding city: ${cityName}`);

  // Try services in order of preference
  const services = ['google', 'nominatim', 'mapbox', 'backend'];
  
  for (const serviceName of services) {
    try {
      console.log(`🔍 Trying ${serviceName} geocoding for: ${cityName}`);
      const coordinates = await geocodingServices[serviceName](cityName);
      
      // Cache the result
      coordinateCache.set(normalizedName, coordinates);
      console.log(`✅ ${serviceName} geocoding successful for ${cityName}:`, coordinates);
      
      return coordinates;
    } catch (error) {
      console.warn(`⚠️ ${serviceName} geocoding failed for ${cityName}:`, error.message);
      // Continue to next service
    }
  }

  // If all services fail, throw error
  throw new Error(`All geocoding services failed for: ${cityName}`);
};

// Batch geocoding for multiple cities
export const getMultipleCityCoordinates = async (cityNames) => {
  const results = {};
  const errors = {};

  for (const cityName of cityNames) {
    try {
      results[cityName] = await getCityCoordinates(cityName);
    } catch (error) {
      errors[cityName] = error.message;
    }
  }

  return { results, errors };
};

// Clear cache (useful for testing or when you want fresh data)
export const clearCoordinateCache = () => {
  coordinateCache.clear();
  console.log('🗑️ Coordinate cache cleared');
};

// Get cache statistics
export const getCacheStats = () => {
  return {
    size: coordinateCache.size,
    keys: Array.from(coordinateCache.keys())
  };
};

// Preload coordinates for common cities
export const preloadCommonCities = async () => {
  const commonCities = [
    'Paris, France',
    'London, UK', 
    'New York, USA',
    'Tokyo, Japan',
    'Sydney, Australia',
    'Dubai, UAE',
    'Rome, Italy',
    'Barcelona, Spain',
    'Amsterdam, Netherlands',
    'Berlin, Germany'
  ];

  console.log('🔄 Preloading coordinates for common cities...');
  
  const { results, errors } = await getMultipleCityCoordinates(commonCities);
  
  console.log(`✅ Preloaded ${Object.keys(results).length} cities`);
  if (Object.keys(errors).length > 0) {
    console.warn(`⚠️ Failed to geocode ${Object.keys(errors).length} cities:`, errors);
  }
  
  return { results, errors };
};
